import batchService from 'ApiService/Admin/batchService';
import studentService from 'ApiService/Admin/studentService';
import DateField from 'components/fields/dateField';
import InputField from 'components/fields/InputField';
import TextField from 'components/fields/TextField';
import { AuthContext } from 'contexts/AuthContext';
import ConfirmationModal from 'pages/Common/ConfirmationModal';
import { deepConvertDatesForApi } from 'pages/helpers/convertDatesForApi';
import { useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
const BatchSwap = ({
  student,
  currentBatchId,
  handleBackClick,
  setCurrentStep,
  currentCourseId,
  courses,
  selectedBranch,
}) => {
  const [selectedCourse, setSelectedCourse] = useState(currentCourseId);
  const [selectedBatchStructure, setSelectedBatchStructure] = useState('');
  const [selectedBatch, setSelectedBatch] = useState('');
  const [batchStructures, setBatchStructures] = useState([]);
  const [availableBatches, setAvailableBatches] = useState([]);
  const [loadingStructures, setLoadingStructures] = useState(false);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  const [disable, setDisable] = useState(true);
  const [balanceMessage, setBalanceMessage] = useState('');
  const [confirmationMessage, setConfirmationMessage] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [feeData, setFeeData] = useState({
    totalAmount: '',
    isFullPayment: true,
    installments: [],
  });
  // Fetch batch structures when course changes
  const fetchBatchStructures = async (courseId) => {
    if (!courseId) return;

    setLoadingStructures(true);
    try {
      const response = await batchService.getBatchStructuresByCourse(courseId);
      if (response.data.success) {
        setBatchStructures(response.data.batchStructures || []);
      } else {
        toast.error('Failed to fetch batch structures.');
        setBatchStructures([]);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Failed to fetch batch structures.'
      );
      setBatchStructures([]);
    } finally {
      setLoadingStructures(false);
    }
  };
  // Fetch batches when structure changes
  const fetchBatchesByStructure = async (structureId) => {
    if (!structureId) return;

    setLoadingBatches(true);
    try {
      const response = await batchService.getBatchesByBatchStructure(
        structureId,
        selectedBranch
      );
      if (response.data.success) {
        let batches = response.data.batches || [];
        // Filter out current batch if in same course
        if (selectedCourse === currentCourseId) {
          batches = batches.filter((batch) => batch._id !== currentBatchId);
        }
        setAvailableBatches(batches);
      } else {
        toast.error('Failed to fetch batches.');
        setAvailableBatches([]);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to fetch batches.');
      setAvailableBatches([]);
    } finally {
      setLoadingBatches(false);
    }
  };
  // Initial fetch if course is already selected
  useEffect(() => {
    if (selectedCourse) {
      fetchBatchStructures(selectedCourse);
    }
  }, []);
  const handleCourseChange = async (event) => {
    const newCourseId = event.target.value;
    setSelectedCourse(newCourseId);
    setSelectedBatchStructure('');
    setSelectedBatch('');
    setAvailableBatches([]);

    if (newCourseId) {
      await fetchBatchStructures(newCourseId);
    } else {
      setBatchStructures([]);
    }
  };
  const handleStructureChange = async (event) => {
    const structureId = event.target.value;
    setSelectedBatchStructure(structureId);
    setSelectedBatch('');

    if (structureId) {
      await fetchBatchesByStructure(structureId);
    } else {
      setAvailableBatches([]);
    }
  };
  const handleBatchChange = (event) => {
    setSelectedBatch(event.target.value);
  };
  const handleFeesChange = (field, value) => {
    setFeeData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };
  const addInstallment = () => {
    setFeeData((prev) => ({
      ...prev,
      installments: [
        ...prev.installments,
        {
          amount: '',
          dueDate: '',
          isPaid: false,
          isEmiInstallment: false,
        },
      ],
    }));
  };
  const removeInstallment = (index) => {
    setFeeData((prev) => ({
      ...prev,
      installments: prev.installments.filter((_, idx) => idx !== index),
    }));
  };
  const handleInstallmentChange = (index, field, value) => {
    setFeeData((prev) => {
      const newInstallments = [...prev.installments];
      newInstallments[index] = {
        ...newInstallments[index],
        [field]: value,
      };
      return {
        ...prev,
        installments: newInstallments,
      };
    });
  };
  const handleSwap = async () => {
    if (!selectedBatch || !selectedCourse || !feeData.totalAmount || !reason) {
      toast.error('Course, Batch, Fee, and Reason are mandatory');
      return;
    }
    const selectedCourseDetails = courses.find(
      (course) =>
        course._id === selectedCourse || course.courseId === selectedCourse
    );
    const selectedBatchDetails = availableBatches.find(
      (batch) => batch._id === selectedBatch
    );
    const currentCourseDetails = courses.find(
      (course) =>
        course._id === currentCourseId || course.courseId === currentCourseId
    );
    const confirmationMessage = `
Are you sure you want to swap ${student.name}'s batch?
From:
• Course: ${
      currentCourseDetails?.courseName || 'Current Course'
    } - Batch: Current Batch
To:
• Course: ${selectedCourseDetails?.courseName} - Batch: ${
      selectedBatchDetails?.batchNumber
    }
• Start Date: ${new Date(
      selectedBatchDetails?.startDate
    ).toLocaleDateString()} - End Date: ${new Date(
      selectedBatchDetails?.endDate
    ).toLocaleDateString()}
Fee Details:
• Total Amount: ₹${feeData.totalAmount}
• Payment Type: ${feeData.isFullPayment ? 'Full Payment' : 'Installments'}
${
  !feeData.isFullPayment
    ? `• Number of Installments: ${feeData.installments.length}`
    : ''
}
Reason: ${reason}
${notes ? `\nAdditional Notes: ${notes}` : ''}
    `;
    setConfirmationMessage(confirmationMessage);
    setIsModalOpen(true);
  };
  const confirmCreation = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const dateFields = ['dueDate'];
      const formattedData = deepConvertDatesForApi(feeData, dateFields);
      const payload = {
        studentId: student._id,
        oldBatchId: currentBatchId,
        oldCourseId: currentCourseId,
        newBatchId: selectedBatch,
        newBatchStructureId: selectedBatchStructure,
        newCourseId: selectedCourse,
        reason,
        notes,
        fees: formattedData,
      };
      const response = await studentService.swapBatch(payload);
      if (response.data.success) {
        toast.success(response.data.message);
        setCurrentStep(1);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    const newErrors = [];
    if (!reason.trim()) {
      newErrors.push('Please provide a reason for the swap.');
    }
    if (!feeData.totalAmount) {
      newErrors.push('Please provide a swap fee.');
    }
    if (!selectedBatch) {
      newErrors.push('Please select a batch.');
    }
    // Validate installments if not full payment
    if (!feeData.isFullPayment && feeData.installments.length > 0) {
      const totalInstallments = feeData.installments.reduce(
        (sum, inst) => sum + Number(inst.amount || 0),
        0
      );
      const totalAmount = Number(feeData.totalAmount || 0);
      if (totalInstallments !== totalAmount) {
        newErrors.push('Total installments must equal the total amount.');
      }
      feeData.installments.forEach((inst, index) => {
        if (!inst.amount || !inst.dueDate) {
          newErrors.push(`Installment ${index + 1} is incomplete.`);
        }
      });
    }
    setErrors(newErrors);
    setDisable(newErrors.length > 0);
  }, [reason, feeData, selectedBatch]);
  // Calculate balance message for installments
  useEffect(() => {
    if (!feeData.isFullPayment && feeData.totalAmount) {
      const totalAmount = Number(feeData.totalAmount);
      const currentTotal = feeData.installments.reduce(
        (sum, installment) => sum + Number(installment.amount || 0),
        0
      );
      const remainingAmount = totalAmount - currentTotal;
      if (remainingAmount > 0) {
        setBalanceMessage(
          `Remaining balance to be added in installments: ₹${remainingAmount}`
        );
      } else if (remainingAmount < 0) {
        setBalanceMessage(
          `Warning: Installments exceed total amount by ₹${Math.abs(
            remainingAmount
          )}`
        );
      } else {
        setBalanceMessage('✓ Installments match the total amount perfectly!');
      }
    } else {
      setBalanceMessage('');
    }
  }, [feeData]);
  return (
    <div className="w-full p-4">
      <div className="mb-6 text-center">
        <h4 className="text-lg font-bold text-navy-700 dark:text-white">
          Swap Batch for {student.name}
        </h4>
      </div>
      <div className="space-y-4">
        {/* Course Selection */}
        <div>
          <label className="mb-2 ml-4 block text-sm font-bold text-navy-700 dark:text-white">
            Select Course
          </label>
          <select
            onChange={handleCourseChange}
            value={selectedCourse}
            className="w-full rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
            disabled={loading}
          >
            <option value="">Select Course</option>
            {courses.map((course, i) => (
              <option
                key={course._id || course.courseId}
                value={course._id || course.courseId}
              >
                {i + 1}. {course.courseName}
              </option>
            ))}
          </select>
        </div>
        {/* Batch Structure Selection */}
        <div>
          <label className="mb-2 ml-4 block text-sm font-bold text-navy-700 dark:text-white">
            Select Batch Structure
          </label>
          <select
            onChange={handleStructureChange}
            value={selectedBatchStructure}
            className="w-full rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal outline-none disabled:opacity-50 dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
            disabled={loading || !selectedCourse || loadingStructures}
          >
            <option value="">
              {loadingStructures
                ? 'Loading structures...'
                : !selectedCourse
                ? 'Select course first'
                : 'Select Batch Structure'}
            </option>
            {!loadingStructures &&
              batchStructures.map((bs, i) => (
                <option key={bs._id} value={bs._id}>
                  {i + 1}. {bs.name}
                </option>
              ))}
          </select>
        </div>
        {/* Batch Selection */}
        <div>
          <label className="mb-2 ml-4 block text-sm font-bold text-navy-700 dark:text-white">
            Select New Batch
          </label>
          <select
            onChange={handleBatchChange}
            value={selectedBatch}
            className="w-full rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal outline-none disabled:opacity-50 dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
            disabled={loading || !selectedBatchStructure || loadingBatches}
          >
            <option value="">
              {loadingBatches
                ? 'Loading batches...'
                : !selectedBatchStructure
                ? 'Select structure first'
                : 'Select Batch'}
            </option>
            {!loadingBatches &&
              availableBatches.map((batch) => {
                const times = Array.isArray(batch.daySchedules)
                  ? batch.daySchedules.map((d) => d.startTime).filter(Boolean)
                  : [];
                const uniqueTimes = [...new Set(times)];
                const timeDisplay =
                  uniqueTimes.length > 0
                    ? ` at ${uniqueTimes.join(' / ')}`
                    : '';
                return (
                  <option key={batch._id} value={batch._id}>
                    Batch {batch.batchNumber} - From{' '}
                    {new Date(batch.startDate).toLocaleDateString()} to{' '}
                    {new Date(batch.endDate).toLocaleDateString()}
                    {timeDisplay}
                  </option>
                );
              })}
          </select>
        </div>
        {/* Reason and Notes */}
        <div>
          <TextField
            label="Reason for Swap"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Enter the reason for the swap"
            className="w-full rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
            disabled={loading}
          />
        </div>
        <div>
          <TextField
            label="Additional Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any additional notes here"
            className="w-full rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
            disabled={loading}
          />
        </div>
        {/* Fees Section */}
        <div className="mt-6">
          <h6 className="text-md mb-3 font-semibold">Swap Fees : </h6>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <InputField
                label="Total Amount"
                type="text"
                value={feeData.totalAmount}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*\.?\d*$/.test(value)) {
                    handleFeesChange('totalAmount', value);
                  }
                }}
                maxLength={30}
              />
            </div>
            <div>
              <label className="mb-2 mt-1 block text-sm font-bold text-navy-700 dark:text-white">
                Payment Type
              </label>
              <select
                onChange={(e) =>
                  handleFeesChange('isFullPayment', e.target.value === 'true')
                }
                value={feeData.isFullPayment.toString()}
                className="w-full rounded-xl border border-gray-200 p-3 text-sm outline-none dark:border-white/10 dark:bg-navy-800 dark:text-white"
              >
                <option value="true">Full Payment</option>
                <option value="false">Installments</option>
              </select>
            </div>
          </div>
          {!feeData.isFullPayment && (
            <div className="mt-4">
              {feeData.installments.map((installment, idx) => (
                <div
                  key={idx}
                  className="mb-3 grid grid-cols-1 gap-3 rounded-lg border border-gray-200 p-4 dark:border-white/10 md:grid-cols-3"
                >
                  <div>
                    <InputField
                      label="Amount"
                      type="text"
                      maxLength={30}
                      value={installment.amount}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d*\.?\d*$/.test(value)) {
                          handleInstallmentChange(idx, 'amount', value);
                        }
                      }}
                    />
                  </div>
                  <div>
                    <DateField
                      label="Due Date"
                      type="date"
                      value={installment.dueDate}
                      onChange={(e) =>
                        handleInstallmentChange(idx, 'dueDate', e.target.value)
                      }
                    />
                  </div>
                  <div className="mt-4 flex items-center">
                    <button
                      onClick={() => removeInstallment(idx)}
                      className="ml-auto text-sm text-red-600 hover:text-red-800"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
              {balanceMessage && (
                <div
                  className={`mt-2 ${
                    balanceMessage.includes('Warning')
                      ? 'text-yellow-500'
                      : balanceMessage.includes('✓')
                      ? 'text-green-500'
                      : 'text-blue-500'
                  }`}
                >
                  <p>{balanceMessage}</p>
                </div>
              )}
              <button
                onClick={addInstallment}
                className="rounded-lg bg-brand-500 px-4 py-2 text-white hover:bg-brand-600"
              >
                Add Installment
              </button>
            </div>
          )}
        </div>
        {errors.length > 0 && (
          <div className="text-red-500">
            {errors.map((error, index) => (
              <p key={index}>{error}</p>
            ))}
          </div>
        )}
        {/* Action Buttons */}
        <div className="mt-6 flex justify-end space-x-4">
          <button
            onClick={handleBackClick}
            className="rounded-xl bg-gray-200 px-4 py-2 font-medium text-gray-700 transition duration-200 hover:bg-gray-300 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-600"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleSwap}
            disabled={disable || loading}
            className="rounded-xl bg-brand-900 px-4 py-2 font-medium text-white transition duration-200 hover:bg-brand-800 active:bg-brand-700 dark:bg-brand-400 dark:hover:bg-brand-300 dark:active:opacity-90"
          >
            {loading ? 'Swapping...' : 'Swap Batch'}
          </button>
        </div>
        <ConfirmationModal
          isOpen={isModalOpen}
          onClose={() => {
            if (!loading) setIsModalOpen(false);
          }}
          onConfirm={confirmCreation}
          title="Confirm Batch Swap"
          message={confirmationMessage}
          confirmText={loading ? 'Proceesing...' : 'Yes, Proceed with Swap'}
          cancelText="Cancel"
          icon="warning"
          disabled={loading}
        />
      </div>
    </div>
  );
};
export default BatchSwap;
